import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { MessageSquare, Settings, Users, Clock, Search, LogOut, Eye, Bot, Calendar, BarChart3, Monitor, FileText, User, DollarSign } from "lucide-react";
import ConversationList from "@/components/ConversationList";
import ConversationView from "@/components/ConversationView";
import { TeamManagement } from "@/components/TeamManagement";
import { ForwardingRules } from "@/components/ForwardingRules";
import { BusinessHours } from "@/components/BusinessHours";
import { OrganizationSettings } from "@/components/OrganizationSettings";
import { AISettings } from "@/components/AISettings";
import { KnowledgeBaseManager } from "@/components/KnowledgeBaseManager";
import { AIAnalytics } from "@/components/AIAnalytics";
import { KnowledgeBaseSuggestions } from "@/components/KnowledgeBaseSuggestions";
import { CRMSettings } from "@/components/CRMSettings";
import { ContactManagement } from "@/components/ContactManagement";
import { AppointmentCalendar } from "@/components/AppointmentCalendar";
import { AppointmentList } from "@/components/AppointmentList";
import { UsageMonitor } from "@/components/UsageMonitor";
import { SubscriptionAddons } from "@/components/SubscriptionAddons";
import { CostAnalytics } from "@/components/CostAnalytics";
import { CostAlerts } from "@/components/CostAlerts";
import { WidgetSettings } from "@/components/WidgetSettings";
import { ConversationAnalytics } from "@/components/ConversationAnalytics";
import { MessageTemplates } from "@/components/MessageTemplates";
import { CSATAnalytics } from "@/components/CSATAnalytics";
import { CSATSettings } from "@/components/CSATSettings";
import { WorkflowBuilder } from "@/components/WorkflowBuilder";
import { WorkflowList } from "@/components/WorkflowList";
import { WorkflowAnalytics } from "@/components/WorkflowAnalytics";
import { AccountSettings } from "@/components/AccountSettings";
import { EmailProviderSettings } from "@/components/EmailProviderSettings";
import { SMSProviderSettings } from "@/components/SMSProviderSettings";
import { SMSAutoReplySettings } from "@/components/SMSAutoReplySettings";
import { useAuth } from "@/hooks/useAuth";
import { useConversationStats } from "@/hooks/useConversationStats";
import { useAppointments } from "@/hooks/useAppointments";
import { supabase } from "@/integrations/supabase/client";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { HELP_CONTENT } from "@/lib/help-content";

const Dashboard = () => {
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [calendarView, setCalendarView] = useState<"calendar" | "list">("calendar");
  const [activeTab, setActiveTab] = useState("conversations");
  const { user, loading, isAdmin, signOut } = useAuth();
  const stats = useConversationStats();
  const { todayCount } = useAppointments();
  const navigate = useNavigate();
  const location = useLocation();

  // Update agent availability on mount and cleanup
  useEffect(() => {
    if (user) {
      // Set agent as online
      supabase
        .from('agent_availability')
        .upsert({
          agent_id: user.id,
          is_online: true,
          last_seen_at: new Date().toISOString()
        })
        .then();

      // Update last_seen_at every 30 seconds
      const interval = setInterval(() => {
        supabase
          .from('agent_availability')
          .upsert({
            agent_id: user.id,
            is_online: true,
            last_seen_at: new Date().toISOString()
          })
          .then();
      }, 30000);

      return () => {
        clearInterval(interval);
        // Set agent as offline on unmount
        supabase
          .from('agent_availability')
          .update({ is_online: false })
          .eq('agent_id', user.id)
          .then();
      };
    }
  }, [user]);

  // Handle URL parameter for pre-selecting a conversation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const conversationParam = searchParams.get('conversation');
    
    if (conversationParam) {
      setSelectedConversation(conversationParam);
    }
  }, [location.search]);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold">iQ Texting</h1>
                <p className="text-sm text-muted-foreground">Agent Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="gap-2">
                <div className="h-2 w-2 rounded-full bg-accent animate-pulse-soft" />
                Business Hours Active
              </Badge>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate("/")}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Frontend
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Settings className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => setActiveTab("conversations")}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Conversations
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveTab("calendar")}>
                      <Calendar className="h-4 w-4 mr-2" />
                      Calendar
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveTab("crm")}>
                      <Users className="h-4 w-4 mr-2" />
                      CRM
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveTab("templates")}>
                      <FileText className="h-4 w-4 mr-2" />
                      Templates
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveTab("analytics")}>
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Analytics
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveTab("usage")}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Usage & Billing
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveTab("account")}>
                      <User className="h-4 w-4 mr-2" />
                      Account
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setActiveTab("cost-analytics")}>
                          <DollarSign className="h-4 w-4 mr-2" />
                          Cost Analytics
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setActiveTab("team")}>
                          <Users className="h-4 w-4 mr-2" />
                          Team
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setActiveTab("hours")}>
                          <Clock className="h-4 w-4 mr-2" />
                          Business Hours
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setActiveTab("settings")}>
                          <Settings className="h-4 w-4 mr-2" />
                          Forwarding
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setActiveTab("widget")}>
                          <Monitor className="h-4 w-4 mr-2" />
                          Widget
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setActiveTab("ai-knowledge")}>
                          <Bot className="h-4 w-4 mr-2" />
                          AI Knowledge
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/dashboard/blog")}>
                          <FileText className="h-4 w-4 mr-2" />
                          Blog
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="ghost" size="icon" onClick={signOut}>
                  <LogOut className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  Active Chats
                  <HelpTooltip {...HELP_CONTENT.dashboard.activeChats} side="bottom" />
                </p>
                <p className="text-2xl font-bold text-primary">{stats.active}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-primary/40" />
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  Waiting
                  <HelpTooltip {...HELP_CONTENT.dashboard.waiting} side="bottom" />
                </p>
                <p className="text-2xl font-bold text-accent">{stats.waiting}</p>
              </div>
              <Clock className="h-8 w-8 text-accent/40" />
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  Agents Online
                  <HelpTooltip {...HELP_CONTENT.dashboard.agentsOnline} side="bottom" />
                </p>
                <p className="text-2xl font-bold">{stats.agentsOnline}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground/40" />
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  Today's Total
                  <HelpTooltip {...HELP_CONTENT.dashboard.todaysTotal} side="bottom" />
                </p>
                <p className="text-2xl font-bold">{stats.todayTotal}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-muted-foreground/40" />
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-card to-muted/20 border-0 shadow-[var(--shadow-soft)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  Today's Appointments
                  <HelpTooltip {...HELP_CONTENT.dashboard.todaysAppointments} side="bottom" />
                </p>
                <p className="text-2xl font-bold">{todayCount}</p>
              </div>
              <Calendar className="h-8 w-8 text-muted-foreground/40" />
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">

          <TabsContent value="conversations">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Conversations List */}
              <Card className="lg:col-span-1 p-0 border-0 shadow-[var(--shadow-soft)] overflow-hidden">
                <div className="p-4 border-b bg-muted/30">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search conversations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <ConversationList
                  selectedId={selectedConversation}
                  onSelect={setSelectedConversation}
                />
              </Card>

              {/* Conversation View */}
              <div className="lg:col-span-2">
                {selectedConversation ? (
                  <ConversationView conversationId={selectedConversation} />
                ) : (
                  <Card className="h-[600px] flex items-center justify-center border-0 shadow-[var(--shadow-soft)]">
                    <div className="text-center">
                      <MessageSquare className="h-16 w-16 text-muted-foreground/40 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No conversation selected</h3>
                      <p className="text-muted-foreground">
                        Select a conversation to view messages
                      </p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="calendar">
            <div className="flex justify-end mb-4">
              <Tabs value={calendarView} onValueChange={(v) => setCalendarView(v as "calendar" | "list")}>
                <TabsList>
                  <TabsTrigger value="calendar">Calendar View</TabsTrigger>
                  <TabsTrigger value="list">List View</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            {calendarView === "calendar" ? <AppointmentCalendar /> : <AppointmentList />}
          </TabsContent>

          <TabsContent value="crm">
            <div className="space-y-6">
              {isAdmin && <CRMSettings />}
              <ContactManagement />
            </div>
          </TabsContent>

          <TabsContent value="templates">
            <MessageTemplates />
          </TabsContent>

          <TabsContent value="analytics">
            <ConversationAnalytics />
          </TabsContent>

          <TabsContent value="usage">
            <div className="space-y-6">
              <UsageMonitor />
              <SubscriptionAddons />
            </div>
          </TabsContent>

          <TabsContent value="account">
            <div className="space-y-6">
              <AccountSettings />
              {isAdmin && (
                <>
                  <SMSProviderSettings />
                  <SMSAutoReplySettings />
                  <EmailProviderSettings />
                </>
              )}
            </div>
          </TabsContent>

          {isAdmin && (
            <TabsContent value="cost-analytics">
              <div className="space-y-6">
                <CostAnalytics />
                <CostAlerts />
              </div>
            </TabsContent>
          )}

          {isAdmin && (
            <>
              <TabsContent value="team">
                <TeamManagement />
              </TabsContent>

              <TabsContent value="hours">
                <div className="space-y-6">
                  <OrganizationSettings />
                  <BusinessHours />
                </div>
              </TabsContent>

              <TabsContent value="settings">
                <ForwardingRules />
              </TabsContent>

              <TabsContent value="widget">
                <WidgetSettings />
              </TabsContent>

              <TabsContent value="ai-knowledge">
                <div className="space-y-6">
                  <AISettings />
                  <KnowledgeBaseManager />
                  <KnowledgeBaseSuggestions />
                  <AIAnalytics />
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
    </>
  );
};

export default Dashboard;
